// src/modules/Onboarding/runtime/types.ts
//
// Single type-only contract for the Onboarding runtime. Vue-free (only
// `ComputedRef` via `import type`) — safe for external/* to import without
// breaking the manifest generator.
//
// Consumed by:
//   • external/{variables,actions,featureFlags}.ts — `satisfies` + `ctx` typing
//   • useOnboardingBuilderState.ts — ctx assembly, plugin proxy casts
//   • useJetApi.ts — `JetApi` return type

import type { ComputedRef } from 'vue'

// ── Vue plugin handles ───────────────────────────────────────────────────────
// `$analytic` and `$absmartly` are Vue 2 plugins still accessed via the
// instance proxy (`getCurrentInstance().proxy.$xxx`).

export interface AnalyticPlugin {
	logEvent: (name: string, properties?: Record<string, unknown>) => void
	setUserProperties: (props: Record<string, unknown>) => void
}

export interface ABSmartlyPlugin {
	treatment: (key: string) => number
	peek: (key: string) => number
	attributes: (attrs: Record<string, unknown>) => void
}

// ── Jet onboarding analytics ────────────────────────────────────────────────
// Mirrors `useOnboardingStepEvents()` plus the canonical constants and helper
// maps assembled by `useJetApi`.

interface JetStepEvents {
	currentStepId: string | null
	currentTitle: string | null
	previousStepId: string | null
	previousTitle: string | null
	isBackwards: boolean
	lastAnswerData: unknown
}

export interface JetApi {
	stepEvents: JetStepEvents
	jetStepShow: (screen: unknown, isBackwards?: boolean, isVariation?: boolean) => void
	jetStepDone: (isBackwards: boolean) => void
	jetPopupShow: (modal: unknown) => void
	jetPopupDone: (modal: unknown, closeType: number) => void
	jetButtonClick: (buttonName: string) => void
	jetHandleDelegatedClick: (event: Event) => void
	jetAlertDone: (alertDescription: string, data: unknown) => void
	jetScreenSpecificEvent: (eventName: string, properties: Record<string, unknown>) => void
	jetCaptureAnswer: (data: { name: string; properties: Record<string, unknown> }) => void
	BUTTON_NAME: {
		PRIVACY_POLICY: string
		TERMS_OF_USE: string
		LANGUAGE_SELECTOR: string
	}
	JET_SCREEN_EVENTS: Set<string>
	LEGACY_TO_JET_EVENT: Record<string, { jetName: string; valueKey: string }>
	CUSTOM_EVENT_NAME: {
		HEIGHT_FORM_ERROR: string
	}
	SCREEN_DONE_TO_JET: Record<string, string>
	closeReasonToType: (reason?: string) => number
}

// ── Host operations surface ──────────────────────────────────────────────────
// Complex actions (onDataChanged, onEventTriggered) delegate to the stateful
// composable through this narrow interface. Implementation lives in
// useOnboardingBuilderState.ts; external/actions only calls via ctx.host.*.

interface HostOperations {
	processDataChange: (data: Record<string, unknown>) => void
	processEvent: (data: { name: string; properties: Record<string, unknown>; isUserProperty?: boolean }) => void
}

// ── Store shapes (trimmed to what external/* actually reads) ─────────────────

interface ExternalMainStore {
	user: { email?: string } | null
	email: string | null
	hasPrevSurvey: boolean
	getCountryCode: string
	setLanguageModalState: (payload: { showModal: boolean; screenName: string }) => void
}

interface ExternalSurveyStore {
	getPropertyValue: (key: string, isData?: boolean) => unknown
}

interface ExternalUserFlagsStore {
	getIsOrganicTrafficOnboarding: boolean
}

interface ExternalLocationStore {
	isCrmAutologin: boolean
	getQueryParam: (param: string) => string | undefined
}

// ── ExternalCtx — single argument passed to every variable/action/flag ───────

export interface ExternalCtx {
	mainStore: ExternalMainStore
	surveyStore: ExternalSurveyStore
	userFlagsStore: ExternalUserFlagsStore
	locationStore: ExternalLocationStore
	$analytic: AnalyticPlugin
	jet: JetApi
	computed: {
		weightLossBlockers: ComputedRef<string>
		followingStatementAvg: ComputedRef<number>
	}
	host: HostOperations
}

// ── Type tags shipped in runtime-manifest.json ───────────────────────────────
// Screen-condition authors need to know what shape each variable returns so
// they can pick valid operators (=, >, includes, …). We derive the tag from
// the `resolve` return type via `TagFor<T>` and enforce it at compile time
// through the `defineVariable` / `defineFeatureFlag` helpers below. Unions
// degrade to `'unknown'` — intentional: the manifest consumer can't narrow
// them, so the operator set is reduced.

type VariableTypeTag = 'boolean' | 'number' | 'string' | 'boolean[]' | 'number[]' | 'string[]' | 'unknown'

type FeatureFlagTypeTag = 'boolean' | 'number'

// Tag → value-type: the call-site picks the tag literal, then TS infers Tag
// and constrains `resolve` to return the matching shape. Inverted from a
// "T → Tag" direction because generic inference through conditional types on
// the return value is unreliable (`TagFor<T>` collapses to one branch).

type ValueForVariableTag<Tag extends VariableTypeTag> = Tag extends 'boolean'
	? boolean
	: Tag extends 'number'
		? number
		: Tag extends 'string'
			? string
			: Tag extends 'boolean[]'
				? boolean[]
				: Tag extends 'number[]'
					? number[]
					: Tag extends 'string[]'
						? string[]
						: unknown

type ValueForFlagTag<Tag extends FeatureFlagTypeTag> = Tag extends 'boolean'
	? boolean
	: Tag extends 'number'
		? number
		: never

// ── Described spec shapes ────────────────────────────────────────────────────
// Each external entry is `{ description, [type,] resolve }`. `description` is
// a one-line English string shipped in the manifest for the builder UI.

interface DescribedVariable<Tag extends VariableTypeTag = VariableTypeTag> {
	/** English one-liner — what this variable represents. Shipped in runtime-manifest. */
	description: string
	type: Tag
	resolve: (ctx: ExternalCtx) => ValueForVariableTag<Tag>
}

interface DescribedAction<
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	F extends (...args: any[]) => unknown = (...args: any[]) => unknown,
> {
	/** English one-liner — what this action does when the renderer invokes it. */
	description: string
	resolve: (ctx: ExternalCtx) => F
}

interface DescribedFeatureFlag<Tag extends FeatureFlagTypeTag = FeatureFlagTypeTag> {
	/** English one-liner — what condition this flag expresses. */
	description: string
	type: Tag
	resolve: (ctx: ExternalCtx) => ValueForFlagTag<Tag>
}

// ── Category contracts (used via `satisfies` in external/*) ──────────────────
// The map-level check only asserts "each entry is a described spec". The
// per-entry `type`-vs-`resolve` link is enforced inside `defineVariable<T>` /
// `defineFeatureFlag<T>` / `defineAction<F>`, where T is inferred from the
// individual `resolve` body. Parametrizing the map with `<any>` would collapse
// `TagFor<any>` to a single tag (TS picks the first conditional branch), so we
// erase the generic here and use a plain shape instead.

interface AnyDescribedVariable {
	description: string
	type: VariableTypeTag
	resolve: (ctx: ExternalCtx) => unknown
}

interface AnyDescribedAction {
	description: string
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	resolve: (ctx: ExternalCtx) => (...args: any[]) => unknown
}

interface AnyDescribedFeatureFlag {
	description: string
	type: FeatureFlagTypeTag
	resolve: (ctx: ExternalCtx) => boolean | number
}

export type VariablesMap = Record<string, AnyDescribedVariable>
export type ActionsMap = Record<string, AnyDescribedAction>
export type FeatureFlagsMap = Record<string, AnyDescribedFeatureFlag>

// ── `define*` helpers ────────────────────────────────────────────────────────
// Without these, TS can't infer T per-entry from `resolve` and match it against
// the declared `type` tag. With them, each key's T is inferred from the factory
// body and the tag becomes a compile-time constraint:
//
//   hasEmail: defineVariable({
//     description: '…',
//     type: 'string',                    // ❌ Type '"string"' not assignable to '"boolean"'
//     resolve: (ctx) => Boolean(…),
//   })

export function defineVariable<Tag extends VariableTypeTag>(spec: {
	description: string
	type: Tag
	resolve: (ctx: ExternalCtx) => ValueForVariableTag<Tag>
}): DescribedVariable<Tag> {
	return spec
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function defineAction<F extends (...args: any[]) => unknown>(spec: {
	description: string
	resolve: (ctx: ExternalCtx) => F
}): DescribedAction<F> {
	return spec
}

export function defineFeatureFlag<Tag extends FeatureFlagTypeTag>(spec: {
	description: string
	type: Tag
	resolve: (ctx: ExternalCtx) => ValueForFlagTag<Tag>
}): DescribedFeatureFlag<Tag> {
	return spec
}
